import { ChevronRight, LayoutGrid, List, Search } from "lucide-react";

import { AriIcon } from "@/components/sidebar/icons";
import { Button, Input, ToggleGroup } from "@/components/ui";
import { VAULT_AI_SUGGESTIONS } from "@/mockData/vaultNav";
import type { BreadcrumbSegment } from "@/types/vault.types";
import type { VaultSortId, VaultViewMode } from "@/types/vaultNav.types";
import { cn } from "@/lib/utils";
import { AutoResize } from "@/components/primitives/auto-resize";

interface VaultToolbarProps {
  breadcrumbs: BreadcrumbSegment[];
  onBreadcrumbClick: (path: string[]) => void;
  search: string;
  onSearchChange: (value: string) => void;
  viewMode: VaultViewMode;
  onViewModeChange: (mode: VaultViewMode) => void;
  sortBy: VaultSortId;
  onSortChange: (sort: VaultSortId) => void;
  aiOpen: boolean;
  onAiToggle: () => void;
}

export function VaultToolbar({
  breadcrumbs,
  onBreadcrumbClick,
  search,
  onSearchChange,
  viewMode,
  onViewModeChange,
  sortBy,
  onSortChange,
  aiOpen,
  onAiToggle,
}: VaultToolbarProps) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-5 py-3">
      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1 text-sm">
        {breadcrumbs.map((crumb, i) => (
          <span key={`${crumb.label}-${i}`} className="flex min-w-0 items-center gap-1">
            {i > 0 && <ChevronRight className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />}
            {i === breadcrumbs.length - 1 ? (
              <span className="truncate font-semibold text-foreground">{crumb.label}</span>
            ) : (
              <button
                type="button"
                className="truncate rounded px-1 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => onBreadcrumbClick(crumb.path)}
              >
                {crumb.label}
              </button>
            )}
          </span>
        ))}
      </nav>

      <Input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search files…"
        className="h-8 w-60 max-w-full"
        startIcon={<Search className="size-3.5 text-muted-foreground" aria-hidden="true" />}
        aria-label="Search files"
      />

      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 text-xs overflow-hidden flex items-center justify-center"
        onClick={() => onSortChange(sortBy === "modified" ? "name" : "modified")}
        aria-label="Sort files"
      >
        <AutoResize className="inline-flex items-center gap-1.5" property="width">
          <List className="size-3" aria-hidden="true" />
          {sortBy === "modified" ? "Modified" : "Name"}
        </AutoResize>
      </Button>

      <ToggleGroup
        value={[viewMode]}
        onValueChange={(v) => {
          const next = v[0] as VaultViewMode | undefined;
          if (next) onViewModeChange(next);
        }}
        className="rounded-lg border border-border p-0.5"
      >
        <ToggleGroup.Item value="grid" aria-label="Grid view" className="size-7 px-0">
          <LayoutGrid className="size-3.5" />
        </ToggleGroup.Item>
        <ToggleGroup.Item value="list" aria-label="List view" className="size-7 px-0">
          <List className="size-3.5" />
        </ToggleGroup.Item>
      </ToggleGroup>

      <Button
        variant="ari"
        size="sm"
        rounded="full"
        className={cn("gap-1.5", aiOpen && "")}
        onClick={onAiToggle}
        aria-pressed={aiOpen}
        aria-label="Toggle Ari suggestions"
      >
        <AriIcon className="size-3.5" aria-hidden="true" />
        Ari ({VAULT_AI_SUGGESTIONS.length})
      </Button>
    </div>
  );
}
